import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'

interface Machine {
  id: number
  name: string
  location?: string
  hal?: string
}

export const ProductInspection: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null)
  const [machines, setMachines] = useState<Machine[]>([])
  const [showMachineSelection, setShowMachineSelection] = useState(true)
  const [formData, setFormData] = useState({
    datetime: new Date().toLocaleString('nl-NL'),
    employee_name: '',
    product_number: '',
    current_weight: '',
    control_weight: '',
    comments: '',
    meets_requirements: true,
    pallet_check: false,
    label_check: false,
    sticker_check: false
  })
  const [photo, setPhoto] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [errors, setErrors] = useState<{[key: string]: string}>({})

  useEffect(() => {
    loadMachines()
  }, [])

  const loadMachines = async () => {
    try {
      const { data, error } = await supabase
        .from('machines')
        .select('*')
        .order('name')

      if (error) {
        console.error('Error loading machines:', error)
        return
      }

      setMachines(data || [])
    } catch (error) {
      console.error('Error loading machines:', error)
    }
  }

  const handleMachineSelect = (machine: Machine) => {
    setSelectedMachine(machine)
    setShowMachineSelection(false)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setPhoto(file)
      const reader = new FileReader()
      reader.onload = () => {
        setPhotoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {}

    if (!formData.employee_name.trim()) {
      newErrors.employee_name = 'Medewerker naam is verplicht'
    }
    if (!formData.product_number.trim()) {
      newErrors.product_number = 'Productnummer is verplicht'
    }
    if (!formData.current_weight || parseFloat(formData.current_weight) <= 0) {
      newErrors.current_weight = 'Geldig huidig gewicht is verplicht'
    }
    if (!formData.control_weight || parseFloat(formData.control_weight) <= 0) {
      newErrors.control_weight = 'Geldig controle gewicht is verplicht'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      let photoUrl = null
      
      if (photo) {
        const reader = new FileReader()
        photoUrl = await new Promise<string>((resolve) => {
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(photo)
        })
      }

      const inspectionData = {
        machine_id: selectedMachine?.id.toString(),
        machine_code: selectedMachine?.name,
        employee_name: formData.employee_name,
        product_number: formData.product_number,
        current_weight: parseFloat(formData.current_weight),
        control_weight: parseFloat(formData.control_weight),
        comments: formData.comments || null,
        photo_url: photoUrl,
        meets_requirements: formData.meets_requirements,
        pallet_check: formData.pallet_check,
        label_check: formData.label_check,
        sticker_check: formData.sticker_check,
        teamleader_id: user?.id || null,
        created_by: user?.id || 'unknown',
        created_by_name: user?.name || 'Onbekend'
      }

      const { error } = await supabase
        .from('quality_control')
        .insert([inspectionData])

      if (error) {
        console.error('Error submitting inspection:', error)
        alert('Er ging iets mis bij het versturen van de inspectie.')
        return
      }

      setShowSuccess(true)

      // Auto close success message and redirect
      setTimeout(() => {
        setShowSuccess(false)
        navigate('/dashboard/teamleader')
      }, 2000)

    } catch (error) {
      console.error('Error submitting inspection:', error)
      alert('Er ging iets mis bij het versturen van de inspectie.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (showMachineSelection) {
    return (
      <div className="form-page min-h-screen py-6 sm:py-10 px-3 sm:px-4">
        <div className="form-surface max-w-4xl mx-auto p-4 sm:p-7">
          <p className="eyebrow">Kwaliteitscontrole</p>
          <h1 className="page-title mt-2 mb-1">Selecteer een machine</h1>
          <p className="text-center text-gray-600 mb-6 sm:mb-8">Selecteer de machine voor productinspectie:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {machines.map(machine => (
              <button
                key={machine.id}
                onClick={() => handleMachineSelect(machine)}
                className="p-4 min-h-touch bg-white border-2 border-[#920000] rounded-lg shadow hover:bg-[#920000] hover:text-white transition duration-300 text-center"
              >
                <h3 className="font-bold text-lg">{machine.name}</h3>
                {machine.location && <p className="text-sm mt-1">{machine.location}</p>}
                {machine.hal && <p className="text-xs mt-1">Hal: {machine.hal}</p>}
              </button>
            ))}
          </div>

          <div className="flex justify-center mt-8">
            <button 
              onClick={() => navigate('/dashboard/teamleader')}
              className="px-6 py-3 bg-gray-600 text-white font-medium rounded-lg shadow hover:bg-gray-700 transition duration-300"
            >
              Terug naar Dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="form-page min-h-screen py-6 sm:py-10 px-3 sm:px-4">
      {/* Success Popup */}
      {showSuccess && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50"></div>
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-white p-6 rounded-xl shadow-xl border-2 border-[#920000] w-[calc(100%-2rem)] max-w-sm">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#920000] mx-auto flex items-center justify-center">
                <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-[#920000] mt-4">Succesvol opgeslagen!</h2>
              <p className="text-gray-600 mt-2">Kwaliteitscontrole is voltooid.</p>
            </div>
          </div>
        </>
      )}

      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#920000] mx-auto mb-4"></div>
            <p>Bezig met verzenden...</p>
          </div>
        </div>
      )}

      <div className="form-surface max-w-2xl mx-auto p-4 sm:p-7">
        <p className="eyebrow">Kwaliteitscontrole</p>
        <h1 className="page-title mt-2 mb-6">Inspectie registreren</h1>
        
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="text-sm"><strong>Geselecteerde Machine:</strong> {selectedMachine?.name}</div>
          <div className="text-sm"><strong>Unieke Code:</strong> {selectedMachine?.id}</div>
        </div>

        <form onSubmit={handleSubmit} className="professional-form space-y-4">
          <div>
            <label htmlFor="datetime" className="block text-[#920000] font-bold mb-1">Datum en Tijd:</label>
            <input 
              type="text" 
              id="datetime"
              value={formData.datetime}
              readOnly 
              className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>

          <div>
            <label htmlFor="employee_name" className="block text-[#920000] font-bold mb-1">Welke medewerker staat aan de machine?</label>
            <input 
              type="text" 
              id="employee_name"
              name="employee_name"
              value={formData.employee_name}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#920000]/50"
            />
            {errors.employee_name && <div className="text-red-500 text-sm mt-1">{errors.employee_name}</div>}
          </div>

          <div>
            <label htmlFor="product_number" className="block text-[#920000] font-bold mb-1">Productnummer:</label>
            <input 
              type="text" 
              id="product_number"
              name="product_number"
              value={formData.product_number}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#920000]/50"
            />
            {errors.product_number && <div className="text-red-500 text-sm mt-1">{errors.product_number}</div>}
          </div>

          <div>
            <label htmlFor="current_weight" className="block text-[#920000] font-bold mb-1">Huidige Gewicht (in gram):</label>
            <input 
              type="number" 
              id="current_weight"
              name="current_weight"
              value={formData.current_weight}
              onChange={handleInputChange}
              step="0.1"
              required
              className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#920000]/50"
            />
            {errors.current_weight && <div className="text-red-500 text-sm mt-1">{errors.current_weight}</div>}
          </div>

          <div>
            <label htmlFor="control_weight" className="block text-[#920000] font-bold mb-1">Controle Gewicht (in gram):</label>
            <input 
              type="number" 
              id="control_weight"
              name="control_weight"
              value={formData.control_weight}
              onChange={handleInputChange}
              step="0.1"
              required
              className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#920000]/50"
            />
            {errors.control_weight && <div className="text-red-500 text-sm mt-1">{errors.control_weight}</div>}
          </div>

          <div>
            <label htmlFor="comments" className="block text-[#920000] font-bold mb-1">Opmerkingen:</label>
            <textarea 
              id="comments"
              name="comments"
              value={formData.comments}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#920000]/50"
            />
          </div>

          <div>
            <label htmlFor="photo" className="block text-[#920000] font-bold mb-1">Foto upload:</label>
            <input 
              type="file" 
              id="photo"
              accept="image/*"
              onChange={handlePhotoChange}
              className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#920000]/50"
            />
            {photoPreview && (
              <div className="mt-2">
                <img src={photoPreview} alt="Preview" className="max-w-xs max-h-48 object-contain rounded-lg" />
              </div>
            )}
          </div>

          <div>
            <label htmlFor="meets_requirements" className="block text-[#920000] font-bold mb-1">Voldoet het product aan de eisen?</label>
            <select 
              id="meets_requirements"
              name="meets_requirements"
              value={formData.meets_requirements ? 'true' : 'false'}
              onChange={(e) => setFormData(prev => ({ ...prev, meets_requirements: e.target.value === 'true' }))}
              className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#920000]/50"
            >
              <option value="true">Ja</option>
              <option value="false">Nee</option>
            </select>
          </div>

          <div className="border border-gray-300 rounded-lg p-4">
            <h3 className="text-[#920000] font-bold mb-3">Extra controles:</h3>
            <div className="space-y-2">
              <label className="flex items-center min-h-touch">
                <input 
                  type="checkbox" 
                  name="pallet_check"
                  checked={formData.pallet_check}
                  onChange={handleInputChange}
                  className="mr-2 w-5 h-5 text-[#920000] focus:ring-[#920000]"
                />
                Pallet goed?
              </label>
              <label className="flex items-center min-h-touch">
                <input 
                  type="checkbox" 
                  name="label_check"
                  checked={formData.label_check}
                  onChange={handleInputChange}
                  className="mr-2 w-5 h-5 text-[#920000] focus:ring-[#920000]"
                />
                Etiket goed?
              </label>
              <label className="flex items-center min-h-touch">
                <input 
                  type="checkbox" 
                  name="sticker_check"
                  checked={formData.sticker_check}
                  onChange={handleInputChange}
                  className="mr-2 w-5 h-5 text-[#920000] focus:ring-[#920000]"
                />
                Sticker op octobin?
              </label>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-3 pt-6">
            <button
              type="button"
              onClick={() => setShowMachineSelection(true)}
              className="px-5 py-2 min-h-touch bg-gray-600 text-white font-medium rounded-lg shadow hover:bg-gray-700 transition duration-300"
            >
              Terug
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-3 min-h-touch bg-[#920000] text-white font-medium rounded-lg shadow hover:bg-[#6F0000] transition duration-300 disabled:opacity-50"
            >
              {isSubmitting ? 'Versturen...' : 'Verstuur Kwaliteitscontrole'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
